import { Box, Stack } from '@mui/material'
import { morphForWords, roundingNumber } from 'components/shared/processes'
import { useGetDailyShifts } from 'hook/query/useGetDailyShifts'
import { ShiftOptions } from 'models/ICattlesData'
import type { IMilkingProcessGraph } from 'models/MilkingProcess'
import moment from 'moment'
import { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { COLORS } from 'styles/colors'
import ChartLegend from 'ui/charts/legends/ChartLegend'
import { TypographyPrimary14Bold } from 'ui/typography/TypographyPrimary14Bold'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'
import { localizationApexCharts } from 'utils'
import type {
	ShiftCountsByDate,
	ShiftCountsByTimestamp,
} from '../hooks/useDeviationChartData'

type DeviationChartProps = {
	categories: (number | null)[]
	interpolated: (number | null)[]
	timestamp: number[]
	dateBetweenDates: number
	isLoading: boolean
	milkingGraph?: IMilkingProcessGraph
	isPlaceholder?: boolean
	shiftCountsByDate?: ShiftCountsByDate
	shiftCountsByTimestamp?: ShiftCountsByTimestamp
}

export const DeviationChart = (props: DeviationChartProps) => {
	const {
		categories,
		timestamp,
		dateBetweenDates,
		interpolated,
		isLoading,
		isPlaceholder,
		shiftCountsByDate,
		shiftCountsByTimestamp,
	} = props
	const { data: availableShifts = [] } = useGetDailyShifts({})
	const availableShiftKeys = useMemo(
		() => new Set(availableShifts.map(shift => shift.value)),
		[availableShifts],
	)

	const series: any = [
		{
			name: 'Нет данных',
			data: interpolated,
			color: COLORS.inputBorder,
		},
		{
			name: 'Количество',
			data: categories,
			color: COLORS.error,
		},
	]

	const options: any = {
		mask: false,
		chart: {
			locales: localizationApexCharts(),
			defaultLocale: 'ru',
			zoom: {
				enabled: false,
			},
			height: 250,
			type: 'line',
			toolbar: {
				show: false,
			},
			connectNulls: false,
		},
		markers: {
			size: 0,
			colors: ['#fff'],
			strokeColors: [COLORS.secondary, COLORS.error],
			strokeWidth: 2,
			hover: {
				size: 5,
			},
		},
		stroke: {
			width: 2,
			curve: ['straight', 'straight'],
		},
		title: {
			style: {
				fontSize: '12px',
				fontFamily: 'Montserrat',
				fontStyle: 'normal',
				fontWeight: 400,
				lineHeight: '15px',
				color: '#7F7F84',
			},
			text: 'Количество',
		},
		dataLabels: {
			enabled: false,
		},
		grid: {
			strokeDashArray: [6, 3],
			show: true,
			xaxis: {
				lines: {
					show: true,
				},
			},
		},
		xaxis: {
			crosshairs: {
				show: true,
				opacity: 1,
				position: 'back',
				stroke: {
					width: 1,
					dashArray: 0,
				},
			},
			type: 'category',
			tooltip: {
				enabled: false,
			},
			labels: {
				formatter: function (value) {
					const momentValue = moment(value)
					if (dateBetweenDates === 0) {
						return momentValue.format('HH:mm')
					}

					return momentValue.format('DD.MM')
				},
				rotate: 0,
				rotateAlways: false,
				datetimeUTC: false,
				style: {
					fontSize: '12px',
					fontFamily: 'Montserrat',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '15px',
					color: '#7F7F84',
				},
				datetimeFormatter: {
					year: 'yyyy',
					month: 'MM',
					day: 'dd.MM',
					hour: 'HH:mm',
				},
			},
			categories: timestamp,
		},
		yaxis: {
			tickAmount: 4,
			min: 0,
			labels: {
				style: {
					fontSize: '12px',
					fontFamily: 'Montserrat',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '15px',
					color: '#7F7F84',
				},
				formatter: function (value: number, index: number) {
					if (index % 2 !== 0) {
						return ''
					}
					return roundingNumber(value, 0)
				},
			},
		},
		tooltip: {
			fixed: true,
			custom: ({ series, seriesIndex, dataPointIndex, w }) => {
				const pointTotal = categories[dataPointIndex] as number | null

				if (isPlaceholder || pointTotal === null) {
					const timeValuePlaceholder =
						dateBetweenDates === 0
							? moment(timestamp[dataPointIndex]).format('HH:mm')
							: moment(timestamp[dataPointIndex]).format('DD.MM.YY')

					return `<div
						style='width:max-content; background-color: #fff; border-radius:12px; border: hidden; box-shadow: 0px 0px 16px 0px rgba(4, 6, 6, 0.16); position: relative'>
						<div style='padding:12px 12px; border: none; display:flex; flex-direction:column; gap:4px;'>
							<p style='font-weight:600; font-size:14px; color:#7F7F84; margin:0;'>Нет данных</p>
							<p style='font-weight:400; font-size:12px; color:#7F7F84; margin:0;'>${timeValuePlaceholder}</p>
						</div>
					</div>`
				}

				const timeValue =
					dateBetweenDates === 0
						? moment(timestamp[dataPointIndex]).format('HH:mm')
						: moment(timestamp[dataPointIndex]).format('DD.MM.YY')

				const shiftRow = (count: number, label: string) =>
					`<div style='display:flex; flex-direction:row; align-items:center; gap:4px;'>
						<span style='width:8px; height:8px; border-radius: 50%; background-color: ${COLORS.error};'></span>
						<p style='font-weight:600; font-size:14px; color:#000; margin:0;'>${count}</p>
						<p style='font-weight:400; font-size:12px; color:#7F7F84; margin:0;'>${label}</p>
					</div>`

				const ts = timestamp[dataPointIndex]
				const s = shiftCountsByDate?.[moment(ts).format('YYYY-MM-DD')]
				const rows =
					dateBetweenDates === 0
						? shiftRow(
								pointTotal,
								morphForWords(pointTotal, [
									'отклонение',
									'отклонения',
									'отклонений',
								]),
							)
						: [
								availableShiftKeys.has(ShiftOptions.AM) &&
									shiftRow(
										s?.am ?? 0,
										`${morphForWords(s?.am ?? 0, ['отклонение', 'отклонения', 'отклонений'])} за утреннюю смену`,
									),
								availableShiftKeys.has(ShiftOptions.PM) &&
									shiftRow(
										s?.pm ?? 0,
										`${morphForWords(s?.pm ?? 0, ['отклонение', 'отклонения', 'отклонений'])} за дневную смену`,
									),
								availableShiftKeys.has(ShiftOptions.NIGHT) &&
									shiftRow(
										s?.night ?? 0,
										`${morphForWords(s?.night ?? 0, ['отклонение', 'отклонения', 'отклонений'])} за ночную смену`,
									),
							]
								.filter(Boolean)
								.join('')

				return `<div
					style='width:max-content; background-color: #fff; border-radius:12px; border: hidden; box-shadow: 0px 0px 16px 0px rgba(4, 6, 6, 0.16); position: relative'>
					<div style='padding:16px 8px; border: none;'>
						<div style='display: flex; flex-direction: column; gap:4px'>
							${rows}
							<div style='margin-top:4px;'>
								<p style='font-weight:400; font-size:12px; color:#7F7F84; margin:0;'>${timeValue}</p>
							</div>
						</div>
					</div>
				</div>`
			},
		},
		legend: {
			show: false,
		},
	}

	const descriptionForLegend = [
		{ color: COLORS.error, description: 'Отклонения' },
		{ color: COLORS.secondary, description: 'Нет данных' },
	]

	return (
		<Stack direction={'column'} gap={'16px'}>
			{isLoading ? (
				<Box
					sx={{
						'@keyframes chartPulse': {
							'0%, 100%': { opacity: 0.35 },
							'50%': { opacity: 0.75 },
						},
						animation: 'chartPulse 1.4s ease-in-out infinite',
					}}
				>
					<ReactApexChart
						id={'yieldQtyChart'}
						options={options}
						series={[
							{
								name: 'Количество',
								data: new Array(timestamp.length).fill(null),
								color: COLORS.error,
							},
							{
								name: 'Нет данных',
								data: new Array(timestamp.length).fill(null),
								color: COLORS.secondary,
							},
						]}
						type='line'
						height={250}
					/>
				</Box>
			) : categories?.length === 0 ? (
				<Stack
					direction={'column'}
					alignItems={'center'}
					justifyContent={'center'}
					gap={'12px'}
					sx={{ height: '300px' }}
				>
					<TypographyPrimary14Bold sx={{ color: COLORS.secondary }}>
						Нет данных
					</TypographyPrimary14Bold>
					<TypographySecondary14>
						Нет данных, соответствующих заданным условиям.
					</TypographySecondary14>
				</Stack>
			) : (
				<ReactApexChart
					id={'yieldQtyChart'}
					options={options}
					series={series}
					type='line'
					height={250}
				/>
			)}
			<ChartLegend initialLegends={descriptionForLegend} />
		</Stack>
	)
}
